import type { Platform } from '@/types';
import { getPlatformConfig } from '@/utils/platformDetector';
import {
  SiSpotify,
  SiApplemusic,
  SiSoundcloud,
  SiYoutube,
  SiYoutubemusic,
  SiTiktok,
  SiAmazon,
  SiEtsy,
  SiEbay,
  SiInstagram,
  SiFacebook,
  SiLinkedin,
  SiX,
  SiWhatsapp,
  SiGooglemaps,
  SiGoogledocs,
  SiGooglesheets,
  SiNotion,
  SiSlack,
  SiSnapchat,
  SiPinterest,
  SiReddit,
  SiVimeo,
  SiMessenger,
  SiTelegram,
  SiWechat,
  SiTwitch,
  SiTidal,
  SiShazam,
  SiMeetup,
  SiEventbrite,
  SiIkea,
  SiAliexpress,
  SiPocketcasts,
  SiOvercast,
  SiApplepodcasts,
  SiPandora,
  SiIheartradio,
  SiWalmart,
  SiTarget,
  SiShopee,
  SiSinaweibo,
  SiMercadopago,
  SiCastro,
  SiCastbox,
  SiStitcher,
  SiTicketmaster,
  SiAudiomack,
  SiBeatstars,
  SiBeatport,
  SiLinkfire,
  SiMixcloud,
  SiTunein,
  SiKickstarter,
  SiKick,
  SiBandsintown,
  SiSongkick,
  SiStrava,
  SiThreads,
  SiAirbnb,
  SiBehance,
  SiCalendly,
  SiGofundme,
  SiImgur,
  SiSubstack,
  SiTypeform,
} from 'react-icons/si';
import type { IconType } from 'react-icons';

// Icons from @icons-pack/react-simple-icons for brands missing in react-icons/si
import {
  SiDeezer as SipDeezer,
  SiBookingdotcom as SipBooking,
  SiVinted as SipVinted,
  SiQq as SipQq,
} from '@icons-pack/react-simple-icons';

import { Link2 } from 'lucide-react';

// Wrapper to adapt @icons-pack components (which accept {color, size}) to IconType
const wrapSimpleIcon = (Icon: React.ComponentType<{ color?: string; size?: number }>) => {
  const Wrapped: IconType = ({ size, color, ...rest }) => (
    <Icon size={size as number} color={color as string} {...rest} />
  );
  return Wrapped;
};

const platformIcons: Partial<Record<Platform, IconType>> = {
  // Music & Podcasts
  spotify: SiSpotify,
  'apple-music': SiApplemusic,
  deezer: wrapSimpleIcon(SipDeezer),
  soundcloud: SiSoundcloud,
  youtube: SiYoutube,
  'youtube-music': SiYoutubemusic,
  tidal: SiTidal,
  pandora: SiPandora,
  shazam: SiShazam,
  audiomack: SiAudiomack,
  beatstars: SiBeatstars,
  beatport: SiBeatport,
  mixcloud: SiMixcloud,
  tunein: SiTunein,
  linkfire: SiLinkfire,
  iheartradio: SiIheartradio,
  'apple-podcasts': SiApplepodcasts,
  'pocket-casts': SiPocketcasts,
  overcast: SiOvercast,
  castro: SiCastro,
  castbox: SiCastbox,
  stitcher: SiStitcher,

  // Video
  tiktok: SiTiktok,
  twitch: SiTwitch,
  kick: SiKick,
  vimeo: SiVimeo,
  kickstarter: SiKickstarter,

  // Marketplaces
  amazon: SiAmazon,
  'amazon-music': SiAmazon,
  etsy: SiEtsy,
  ebay: SiEbay,
  walmart: SiWalmart,
  target: SiTarget,
  aliexpress: SiAliexpress,
  ikea: SiIkea,
  shopee: SiShopee,
  vinted: wrapSimpleIcon(SipVinted),
  booking: wrapSimpleIcon(SipBooking),
  'mercado-libre': SiMercadopago,

  // Social
  instagram: SiInstagram,
  facebook: SiFacebook,
  linkedin: SiLinkedin,
  twitter: SiX,
  snapchat: SiSnapchat,
  pinterest: SiPinterest,
  reddit: SiReddit,
  threads: SiThreads,
  weibo: SiSinaweibo,
  strava: SiStrava,
  behance: SiBehance,

  // Messaging
  whatsapp: SiWhatsapp,
  messenger: SiMessenger,
  telegram: SiTelegram,
  wechat: SiWechat,
  qq: wrapSimpleIcon(SipQq),

  // Tools
  'google-maps': SiGooglemaps,
  'google-docs': SiGoogledocs,
  'google-sheets': SiGooglesheets,
  notion: SiNotion,
  slack: SiSlack,

  // Events
  meetup: SiMeetup,
  eventbrite: SiEventbrite,
  ticketmaster: SiTicketmaster,
  bandsintown: SiBandsintown,
  songkick: SiSongkick,

  // Other
  airbnb: SiAirbnb,
  calendly: SiCalendly,
  gofundme: SiGofundme,
  imgur: SiImgur,
  substack: SiSubstack,
  typeform: SiTypeform,
};

interface PlatformIconProps {
  platform: Platform;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const sizeMap = {
  sm: { container: 'w-8 h-8', icon: 16 },
  md: { container: 'w-10 h-10', icon: 20 },
  lg: { container: 'w-12 h-12', icon: 24 },
};

const PlatformIcon = ({ platform, size = 'md', className = '' }: PlatformIconProps) => {
  const config = getPlatformConfig(platform);
  const Icon = platformIcons[platform];
  const { container, icon: iconSize } = sizeMap[size];

  return (
    <div
      className={`${container} rounded-xl flex items-center justify-center flex-shrink-0 ${className}`}
      style={{ backgroundColor: `${config.color}15` }}
    >
      {Icon ? (
        <Icon size={iconSize} color={config.color} />
      ) : (
        <Link2 size={iconSize} color={config.color} />
      )}
    </div>
  );
};

export default PlatformIcon;
